/**
 * Avatar — Visual representation of a user, team, or entity.
 *
 * Avatar Component Guidelines
 * - Pass a `user` object (with `id`, optional `name`, optional `profileImageUrl`)
 *   to render a deterministic illustrated face seeded off the user id, with
 *   the uploaded photo (when present) layered on top. Same id → same face +
 *   brand color forever, so a teammate is recognizable across surfaces.
 * - Alternatively, use `imageSrc` / `name` / `icon` directly for non-user
 *   entities like workspaces, brands, or apps — those render the classic
 *   initial-letter or icon fallback instead of a face.
 * - Use `isSquare` for non-person entities — round avatars imply a person.
 * - Size based on context prominence: `xxs`/`xs` for inline mentions or dense
 *   lists, `sm`/`base` for standard list rows and cards, `lg`/`xl` for profile
 *   headers. For larger surfaces, override the dimensions with
 *   `UNSAFE_className`.
 * - When no `user`, `name`, or `icon` is provided, a dotted-border placeholder
 *   renders — use this for empty/unassigned states (e.g. "add a member").
 * @see https://base-ui.com/react/components/avatar Base UI Avatar documentation
 * @see https://ui.shadcn.com/docs/components/avatar shadcn/ui Avatar documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import { UserFaceFallback } from "@replohq/lattice/components/avatar/UserFaceFallback";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const avatarSizeVariants = cva("", {
  variants: {
    size: {
      xxs: "h-4 w-4",
      xs: "h-5 w-5",
      sm: "h-6 w-6",
      base: "h-9 w-9",
      lg: "h-12 w-12",
      xl: "h-24 w-24",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

const avatarFallbackTextVariants = cva("font-editorial italic", {
  variants: {
    size: {
      xxs: "text-[0.625rem]",
      xs: "text-xs",
      sm: "text-sm",
      base: "text-xl",
      lg: "text-2xl",
      xl: "text-5xl",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

type AvatarSize = "xxs" | "xs" | "sm" | "base" | "lg" | "xl";

type AvatarUser = {
  id: string;
  name?: string | null;
  profileImageUrl?: string | null;
};

type AvatarProps<ClassName extends string = string> = {
  user?: AvatarUser;
  name?: string | null;
  icon?: React.ReactNode;
  imageSrc?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  size?: AvatarSize;
  imageClassName?: string;
  isSquare?: boolean;
};

function AvatarFallback({
  user,
  resolvedName,
  icon,
  size,
  imageClassName,
}: {
  user?: AvatarUser;
  resolvedName?: string | null;
  icon?: React.ReactNode;
  size: AvatarSize;
  imageClassName: string;
}) {
  if (user) {
    return (
      <UserFaceFallback
        seed={user.id}
        name={user.name}
        imageClassName={imageClassName}
      />
    );
  }

  if (resolvedName || icon) {
    return (
      <div
        style={{ borderRadius: "inherit" }}
        className={cn(
          "bg-surface text-default",
          "flex h-full w-full items-center justify-center",
          avatarFallbackTextVariants({ size }),
          imageClassName,
        )}
      >
        {icon ?? resolvedName?.charAt(0)?.toUpperCase() ?? "?"}
      </div>
    );
  }

  return (
    <div
      style={{ borderRadius: "inherit" }}
      className={cn(
        "border-border h-full w-full border-2 border-dotted",
        imageClassName,
      )}
    />
  );
}

function Avatar<ClassName extends string>({
  user,
  imageSrc,
  name,
  icon,
  className,
  UNSAFE_className,
  isSquare,
  size = "base",
  imageClassName = "",
}: AvatarProps<ClassName>) {
  const resolvedName = user?.name ?? name;
  const resolvedImageSrc = user?.profileImageUrl ?? imageSrc;

  const containerClassName = cn(
    "inline-flex items-center justify-items-center align-middle shrink-0 text-white",
    isSquare ? "rounded-xs" : "rounded-full",
    avatarSizeVariants({ size }),
    className,
    UNSAFE_className,
  );

  // Note (Yuxin, 2026-04-11): Always render the colored fallback behind the
  // image so removal is instant — no flash of empty state when the image URL
  // is cleared.
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(containerClassName, "relative overflow-hidden")}
    >
      <AvatarFallback
        user={user}
        resolvedName={resolvedName}
        icon={icon}
        size={size}
        imageClassName={imageClassName}
      />
      {resolvedImageSrc && resolvedName && (
        <AvatarPrimitive.Image
          style={{ borderRadius: "inherit" }}
          className={cn(
            "absolute inset-0 h-full w-full object-cover",
            imageClassName,
          )}
          src={resolvedImageSrc}
          alt={resolvedName}
        />
      )}
    </AvatarPrimitive.Root>
  );
}

export { Avatar };
export type { AvatarProps, AvatarSize, AvatarUser };
